import { CSSProperties, useState, useEffect } from 'react'
import { clsx, styx } from 'modules/Utils'
import Colors from 'modules/Colors'
import useBrand from 'Hooks/useBrand'
import useFacebookConnections from 'Hooks/useFacebookConnections'
import { AdConnectionType } from 'modules/Const';
import CardSymphony from 'elements/CardSymphony';
import ConnectionStep from 'pages/MarketingPage/Components/StepsSocialMedia/ConnectionStep';
import AdConnectionModal from 'components/shareable/AdConnectionModal';
import useFacebookConnectionSteps from 'Hooks/useFacebookConnectionSteps';
import ModalSymphony from 'elements/ModalSymphony';
import InputSymphony from 'elements/InputSymphony'; // Added
import ButtonSymphony from 'elements/ButtonSymphony';
import { toast } from 'react-toastify';
// Box and Typography will be removed by the end of this diff
import { Box, Typography } from '@mui/material';
import { connectToBrand_api } from 'api/brandApi';
import GlobalSty from 'modules/Sty'; // Added GlobalSty
import Sty from 'modules/Sty'
import Intercom from 'helpers/Intercom'

interface ConnectionsTabProps {
    className?: string
    style?: CSSProperties
}

const ConnectionsTab = ({
    className,
    style,
}: ConnectionsTabProps) => {
    const { currentBrand, getBrand, isLoadingBrand } = useBrand();
    const { initializeFB, setPixelConversionsAccessToken } = useFacebookConnections();

    const [selectedAdConnectionType, setSelectedAdConnectionType] = useState<AdConnectionType | null>(null);
    const [apiCallLoading, setApiCallLoading] = useState(false);
    const [isConversionsApiModalOpen, setIsConversionsApiModalOpen] = useState(false);
    const [capiTokenInput, setCapiTokenInput] = useState('');

    // Initialize Facebook connections when component mounts
    useEffect(() => {
        if (currentBrand) {
            initializeFB(currentBrand);
        }
    }, [currentBrand]);

    const handleOpenConversionsApiModal = () => {
        setCapiTokenInput(currentBrand?.connections?.facebook_pixel_conversions_api_access_token || '');
        setIsConversionsApiModalOpen(true);
    };

    const handleCloseConversionsApiModal = () => {
        setIsConversionsApiModalOpen(false);
        // Optionally reset capiTokenInput if desired, or let it persist last entered value until next open
    };

    const handleSaveCapiToken = async () => {
        if (!currentBrand || !currentBrand.id || !currentBrand.slug) {
            toast.error('Brand information is not available.');
            return;
        }
        setApiCallLoading(true);
        try {
            const response = await connectToBrand_api({
                brandId: currentBrand.id,
                brandSlug: currentBrand.slug,
                service: 'facebook_pixel_conversions_api_access_token',
                value: capiTokenInput,
            });

            if (response) {
                await getBrand();
                setPixelConversionsAccessToken(capiTokenInput);
                toast.success('Conversions API Access Token saved successfully!');
                handleCloseConversionsApiModal();
            } else {
                toast.error('Failed to save token. Please try again.');
            }
        } catch (error) {
            console.error('Error saving Conversions API Access Token:', error);
            toast.error('An error occurred. Failed to save token.');
        } finally {
            setApiCallLoading(false);
        }
    };

    const {
        connectionSteps,
        showLogoutModal,
        handleCancelLogout,
        handleConfirmLogout,
        isLoggingOut,
    } = useFacebookConnectionSteps({
        context: 'settings',
        onConnectionTypeSelected: setSelectedAdConnectionType,
        includePixelStep: true,
        onOpenConversionsApiModal: handleOpenConversionsApiModal, // Passed the handler for CAPI modal
    });

    const renderConnectionsBanner = () => {
        return (
            <CardSymphony
                className="connectionsTabBanner"
                title="Default connections for all future campaigns"
                subtitle="These connections will be used as defaults for all new campaigns. If you manage multiple ad accounts, we recommend creating separate Symphony profiles for each account."
                size="small"
                style={{ backgroundColor: Colors.purpleLighter }}
            />
        )
    }

    const renderConnectionSteps = () => {
        return (
            <div className="connectionStepsContainer">
                {connectionSteps.map((step, index) => (
                    <ConnectionStep
                        key={`connection-step-${index}`}
                        stepTitle={step.stepTitle}
                        connectedAccountName={step.connectedAccountName}
                        connectedAccountIcon={step.connectedAccountIcon}
                        stepDescription={step.stepDescription}
                        isCompleted={step.isCompleted}
                        onButtonClick={step.onButtonClick}
                        connectedAccountDescriptions={step.connectedAccountDescriptions}
                        isLoading={step.isLoading}
                        canEdit={step.canEdit}
                        buttonText={step.buttonText}
                        iconContainerColor={step.iconContainerColor}
                        isLastStep={step.isLastStep}
                    />
                ))}
            </div>
        )
    }

    const handleCloseModal = () => {
        setSelectedAdConnectionType(null);
    };

    const renderFacebookConversionsApiTokenModal = () => {
        if (!isConversionsApiModalOpen) return null;

        return (
            <ModalSymphony
                className="facebookConversionsApiTokenModal"
                isOpen={isConversionsApiModalOpen}
                onClose={handleCloseConversionsApiModal}
                title="Conversions API Access Token"
                subtitle="Enter your Facebook Conversions API Access Token."
                size="medium"
                buttonLeftText="Cancel"
                buttonLeftVariant="text"
                buttonLeftIsLoading={isLoadingBrand}
                buttonLeftDisabled={isLoadingBrand}
                buttonRightText="Save Token"
                buttonRightVariant="outlined"
                onClickButtonLeft={handleCloseConversionsApiModal}
                onClickButtonRight={handleSaveCapiToken}
                buttonRightIsLoading={apiCallLoading || isLoadingBrand}
                buttonRightDisabled={apiCallLoading || isLoadingBrand}
                style={{
                    gap: 16,
                }}
            >
                <p style={Sty.text16}>
                    This token allows Symphony to send server-side events to Facebook, improving tracking accuracy, especially around ad blockers.
                </p>
                <InputSymphony
                    className="facebookConversionsApiTokenModalInput"
                    label="Access Token"
                    value={capiTokenInput}
                    onChange={(value: string) => setCapiTokenInput(value)}

                    placeholder="Paste your token here"
                />
                <p style={styx(sty.modalCaptionText, { cursor: 'pointer', textDecoration: 'underline', color: Colors.purpleLight })} onClick={() => Intercom.openWhereIsMyConversionsTokenArticle()}>
                    You can generate an access token in your Facebook Events Manager. Click here to learn how.
                </p>
            </ModalSymphony>
        );
    };

    // NOTE: render()
    return (
        <div
            className={clsx('connectionsTabComponent', className)}
            style={styx(sty.connectionsTabComponent, style)}
        >
            {renderConnectionsBanner()}
            {renderConnectionSteps()}

            <AdConnectionModal
                isOpen={Boolean(selectedAdConnectionType)}
                onClose={handleCloseModal}
                connectionType={selectedAdConnectionType || undefined}
            />

            <ModalSymphony
                className="facebookLogoutConfirmationModal"
                isOpen={showLogoutModal}
                onClose={handleCancelLogout}
                title="Are you sure you want to log out?"
                subtitle="You won't be able to view Meta campaign data until you re-log back in, and your ad account, linked pages, and pixel will also be removed."
                headerIcon="facebook"
                size="small"
                onClickButtonLeft={handleCancelLogout}
                buttonLeftText="Nevermind"
                buttonLeftVariant="text"
                onClickButtonRight={handleConfirmLogout}
                buttonRightText="Yes, log out"
                buttonRightVariant="outlined"
                buttonRightColor="red"
                buttonRightIsLoading={isLoggingOut}
                buttonRightDisabled={isLoggingOut}
            />

            {renderFacebookConversionsApiTokenModal()}
        </div>
    );
};

const sty: Record<string, CSSProperties> = {
    connectionsTabComponent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        width: '100%',
    },
    // Styles for the refactored modal
    modalContentContainer: {
        padding: 24, // Corresponds to sx={{ p: 3 }} (8 * 3 = 24)
    },
    modalBodyText: {
        ...(GlobalSty.text16 as CSSProperties), // Using text16 from GlobalSty
        marginBottom: 16, // Corresponds to sx={{ mb: 2 }} (8 * 2 = 16)
        color: Colors.black, // Ensuring default text color
    },
    inputWrapper: {
        marginBottom: 24, // Corresponds to sx={{ mb: 3 }}
    },
    modalCaptionText: {
        ...(GlobalSty.text12grey as CSSProperties), // Using text12grey for caption like text
        display: 'block',
        marginBottom: 24, // Corresponds to sx={{ mb: 3 }}
    },
    modalActionsContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 8, // Corresponds to gap: 1
    },
};

export default ConnectionsTab;